import {
  AIAgentInsights,
  CRMChannel,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useMemo } from "react";
import { useAgentAnalyticsContext } from "../providers/useAgentAnalyticsContext";

const CHANNELS = [
  CRMChannel.whatsapp,
  CRMChannel.instagram,
  CRMChannel.chatbot,
  CRMChannel.twilio,
  CRMChannel.email,
] as const;

export interface ChatsOverviewKPITotals {
  totalConversations: number;
  totalMessages: number;
  aiReplies: number;
  humanSentMessages: number;
  totalAiConversations: number;
  resolved: number;
  unresolved: number;
  missedAiReply: number;
}

export const useChatsOverviewKPIData = (entityId: string) => {
  const { integrationsApi } = useRestApiProvider();
  const { timeRange } = useAgentAnalyticsContext();

  const {
    data: whatsappInsights,
    isLoading: isWhatsappLoading,
    isFetching: isWhatsappFetching,
    isError: isWhatsappError,
    refetch: whatsappRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.whatsapp,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: instagramInsights,
    isLoading: isInstagramLoading,
    isFetching: isInstagramFetching,
    isError: isInstagramError,
    refetch: instagramRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.instagram,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: chatbotInsights,
    isLoading: isChatbotLoading,
    isFetching: isChatbotFetching,
    isError: isChatbotError,
    refetch: chatbotRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.chatbot,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: twilioInsights,
    isLoading: isTwilioLoading,
    isFetching: isTwilioFetching,
    isError: isTwilioError,
    refetch: twilioRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.twilio,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: gmailInsights,
    isLoading: isGmailLoading,
    isFetching: isGmailFetching,
    isError: isGmailError,
    refetch: gmailoRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.email,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const insightsByChannel: Partial<
    Record<CRMChannel, AIAgentInsights | undefined>
  > = useMemo(
    () => ({
      [CRMChannel.whatsapp]: whatsappInsights,
      [CRMChannel.instagram]: instagramInsights,
      [CRMChannel.chatbot]: chatbotInsights,
      [CRMChannel.twilio]: twilioInsights,
      [CRMChannel.email]: gmailInsights,
    }),
    [
      whatsappInsights,
      instagramInsights,
      chatbotInsights,
      twilioInsights,
      gmailInsights,
    ]
  );

  const totals = useMemo<ChatsOverviewKPITotals | null>(() => {
    const items = CHANNELS.map((ch) => insightsByChannel[ch]);
    if (items.some((x) => x == null)) return null;
    const validItems = items as AIAgentInsights[];
    return validItems.reduce<ChatsOverviewKPITotals>(
      (acc, item) => ({
        totalConversations: acc.totalConversations + item.total_conversations,
        totalMessages:
          acc.totalMessages + item.ai_replies + item.human_sent_messages,
        aiReplies: acc.aiReplies + item.ai_replies,
        humanSentMessages: acc.humanSentMessages + item.human_sent_messages,
        totalAiConversations:
          acc.totalAiConversations + item.total_ai_conversations,
        resolved: acc.resolved + item.resolved,
        unresolved: acc.unresolved + item.unresolved,
        missedAiReply: acc.missedAiReply + item.missed_ai_reply,
      }),
      {
        totalConversations: 0,
        totalMessages: 0,
        aiReplies: 0,
        humanSentMessages: 0,
        totalAiConversations: 0,
        resolved: 0,
        unresolved: 0,
        missedAiReply: 0,
      }
    );
  }, [insightsByChannel]);

  const isLoading =
    isWhatsappLoading ||
    isWhatsappFetching ||
    isInstagramLoading ||
    isInstagramFetching ||
    isChatbotLoading ||
    isChatbotFetching ||
    isTwilioLoading ||
    isTwilioFetching ||
    isGmailLoading ||
    isGmailFetching;

  const isError =
    isWhatsappError ||
    isInstagramError ||
    isChatbotError ||
    isTwilioError ||
    isGmailError;

  const refetch = () => {
    if (isWhatsappError) whatsappRefetch();
    if (isInstagramError) instagramRefetch();
    if (isChatbotError) chatbotRefetch();
    if (isTwilioError) twilioRefetch();
    if (isGmailError) gmailoRefetch();
  };

  return {
    totals,
    insightsByChannel,
    isLoading,
    isError,
    refetch,
  };
};
